import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, UserPlus, Linkedin, Loader2, Briefcase, Target, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOOKING_FOR_OPTIONS = [
  'Investors',
  'Partners',
  'Clients',
  'Suppliers',
  'Mentors',
  'Talent/Recruiting',
  'Media/PR',
  'Collaborators',
  'Industry Experts',
  'Co-founders'
];

const OFFERING_OPTIONS = [
  'Investment',
  'Partnership Opportunities',
  'Services/Solutions',
  'Products',
  'Mentorship',
  'Job Opportunities',
  'Media Coverage',
  'Industry Knowledge',
  'Network Introductions',
  'Technical Expertise'
];

export default function NetworkingSuggestions({ eventId, eventName }) {
  const [step, setStep] = useState('intro'); // intro, register, matches
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState({
    user_email: '',
    user_name: '',
    job_title: '',
    company: '',
    industry: '',
    looking_for: [],
    offering: [],
    bio: '',
    linkedin_url: ''
  });

  const toggleOption = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/genome/networking/stats/${eventId}`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const checkExistingMatches = async () => {
    if (!profile.user_email) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/genome/networking/matches/${eventId}/${encodeURIComponent(profile.user_email)}`
      );
      if (response.data.matches && response.data.matches.length > 0) {
        setMatches(response.data.matches);
        setStep('matches');
      } else {
        setStep('register');
      }
    } catch (error) {
      setStep('register');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!profile.user_email || !profile.user_name || !profile.job_title || !profile.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (profile.looking_for.length === 0) {
      toast.error('Please select at least one thing you are looking for');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/genome/networking/register`, {
        event_id: eventId,
        ...profile
      });

      // Fetch matches after registration
      const matchResponse = await axios.get(
        `${BACKEND_URL}/api/genome/networking/matches/${eventId}/${encodeURIComponent(profile.user_email)}`
      );
      
      setMatches(matchResponse.data.matches || []);
      setStep('matches');
      toast.success('Registered for networking! Here are your matches.');
    } catch (error) {
      toast.error('Failed to register. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Load stats when component mounts or enters intro
  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/genome/networking/stats/${eventId}`);
        setStats(response.data.stats);
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    };
    
    if (step === 'intro') {
      fetchStats();
    }
  }, [step, eventId]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Networking Matchmaker</h3>
            <p className="text-purple-100 text-sm">Connect with the right people at {eventName}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Intro Step */}
        {step === 'intro' && (
          <div className="space-y-6">
            <p className="text-gray-600">
              Get matched with fellow attendees based on your goals. Find investors, partners, clients, and more before the event even starts.
            </p>

            {/* Stats */}
            {stats && stats.total_registered > 0 && (
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm font-medium text-purple-900 mb-2">
                  {stats.total_registered} professionals already registered
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-purple-700 font-medium">Looking For:</p>
                    <ul className="text-purple-600">
                      {stats.top_needs?.slice(0, 3).map(([need, count]) => (
                        <li key={need}>{need} ({count})</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-purple-700 font-medium">Offering:</p>
                    <ul className="text-purple-600">
                      {stats.top_offerings?.slice(0, 3).map(([offer, count]) => (
                        <li key={offer}>{offer} ({count})</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 block">Enter your email to check for matches:</label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={profile.user_email}
                onChange={e => setProfile({ ...profile, user_email: e.target.value })}
              />
              <Button
                onClick={checkExistingMatches}
                disabled={loading || !profile.user_email}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find My Matches'}
              </Button>
            </div>
          </div>
        )}

        {/* Registration Step */}
        {step === 'register' && (
          <div className="space-y-5">
            <p className="text-gray-600">
              Complete your profile to get matched with the right people.
            </p>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name *</label>
                <Input
                  placeholder="Your full name"
                  value={profile.user_name}
                  onChange={e => setProfile({ ...profile, user_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Job Title *</label>
                <Input
                  placeholder="e.g. CEO, Marketing Director"
                  value={profile.job_title}
                  onChange={e => setProfile({ ...profile, job_title: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Company *</label>
                <Input
                  placeholder="Your company name"
                  value={profile.company}
                  onChange={e => setProfile({ ...profile, company: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Industry</label>
                <Input
                  placeholder="e.g. Technology, Finance"
                  value={profile.industry}
                  onChange={e => setProfile({ ...profile, industry: e.target.value })}
                />
              </div>
            </div>

            {/* Looking For */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                <Target className="w-4 h-4 inline mr-1" />
                What are you looking for? *
              </label>
              <div className="flex flex-wrap gap-2">
                {LOOKING_FOR_OPTIONS.map(option => (
                  <button
                    key={option}
                    onClick={() => toggleOption('looking_for', option)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      profile.looking_for.includes(option)
                        ? 'bg-purple-100 border-purple-500 text-purple-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {profile.looking_for.includes(option) && <CheckCircle className="w-3 h-3 inline mr-1" />}
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Offering */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                <Briefcase className="w-4 h-4 inline mr-1" />
                What can you offer?
              </label>
              <div className="flex flex-wrap gap-2">
                {OFFERING_OPTIONS.map(option => (
                  <button
                    key={option}
                    onClick={() => toggleOption('offering', option)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      profile.offering.includes(option)
                        ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {profile.offering.includes(option) && <CheckCircle className="w-3 h-3 inline mr-1" />}
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional fields */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Short Bio (optional)</label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg text-sm"
                rows={2}
                placeholder="Tell others what you do and what makes you interesting..."
                value={profile.bio}
                onChange={e => setProfile({ ...profile, bio: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                <Linkedin className="w-4 h-4 inline mr-1" />
                LinkedIn URL (optional)
              </label>
              <Input
                placeholder="https://linkedin.com/in/yourprofile"
                value={profile.linkedin_url}
                onChange={e => setProfile({ ...profile, linkedin_url: e.target.value })}
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('intro')} className="flex-1">
                Back
              </Button>
              <Button
                onClick={handleRegister}
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find My Matches'}
              </Button>
            </div>
          </div>
        )}

        {/* Matches Step */}
        {step === 'matches' && (
          <div className="space-y-4">
            {matches.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No matches found yet.</p>
                <p className="text-sm text-gray-500">Check back closer to the event as more people register.</p>
              </div>
            ) : (
              <>
                <p className="text-gray-600">
                  Found <span className="font-semibold text-purple-600">{matches.length} matches</span> for you!
                </p>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {matches.map((match, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-4 hover:border-purple-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{match.name}</h4>
                          <p className="text-sm text-gray-600">
                            {match.job_title} at {match.company}
                          </p>
                          {match.industry && (
                            <p className="text-xs text-gray-500">{match.industry}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {match.match_score}% match
                          </span>
                        </div>
                      </div>

                      {match.match_reasons && (
                        <div className="mt-2 text-xs text-gray-500">
                          <span className="font-medium">Why matched:</span> {match.match_reasons.join(', ')}
                        </div>
                      )}

                      {match.bio && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{match.bio}</p>
                      )}

                      {match.linkedin_url && (
                        <a
                          href={match.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Linkedin className="w-4 h-4 mr-1" />
                          View Profile
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <Button variant="outline" onClick={() => setStep('intro')} className="w-full">
              <UserPlus className="w-4 h-4 mr-2" />
              Update My Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
